import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Chrome, AlertCircle } from 'lucide-react';
import { validateDeliverableEmail } from '../utils/emailValidation';

export function SignIn() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signInWithEmail, signUpWithEmail, signInWithGoogle } = useAuth();

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/\d/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    if (!/[@$!%*?&]/.test(pwd)) {
      return 'Password must contain at least one special character (@$!%*?&)';
    }
    return null;
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const emailError = validateDeliverableEmail(email);
    if (emailError) {
      setError(emailError);
      setLoading(false);
      return;
    }

    // Validate password on sign-up
    if (isSignUp) {
      const passwordError = validatePassword(password);
      if (passwordError) {
        setError(passwordError);
        setLoading(false);
        return;
      }
    }

    const { error } = isSignUp
      ? await signUpWithEmail(email, password)
      : await signInWithEmail(email, password);

    if (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError('');
    const { error } = await signInWithGoogle();
    if (error) {
      setError(error.message);
    }
  };

  const handleSocialAuth = async (provider: 'google' | 'facebook' | 'apple') => {
    if (provider === 'google') {
      await handleGoogleSignIn();
      return;
    }

    setError(
      `${provider === 'facebook' ? 'Facebook' : 'Apple'} sign-in still needs provider setup for this project.`
    );
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-dropsugar-green/20 rounded-full filter blur-3xl opacity-40" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-200 rounded-full filter blur-3xl opacity-30" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark p-8 text-center">
            <div className="flex justify-center mb-4">
              <img
                src="/dropsugar-logo.jpg"
                alt="DropSugar"
                className="h-8 w-auto object-contain"
              />
            </div>
            <p className="text-blue-100">Transform your lifestyle with CGM monitoring</p>
          </div>

          <div className="p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="mb-6 space-y-3">
              <button
                onClick={() => handleSocialAuth('google')}
                className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-white border-2 border-gray-300 hover:border-gray-400 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-all shadow-sm hover:shadow"
              >
                <Chrome className="w-5 h-5 text-red-500" />
                <span>{isSignUp ? 'Sign up with Gmail' : 'Continue with Gmail'}</span>
              </button>
              <button
                onClick={() => handleSocialAuth('facebook')}
                className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-white border-2 border-gray-300 hover:border-gray-400 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-all shadow-sm hover:shadow"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                  f
                </span>
                <span>{isSignUp ? 'Sign up with Facebook' : 'Continue with Facebook'}</span>
              </button>
              <button
                onClick={() => handleSocialAuth('apple')}
                className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-white border-2 border-gray-300 hover:border-gray-400 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-all shadow-sm hover:shadow"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white">
                  A
                </span>
                <span>{isSignUp ? 'Sign up with Apple' : 'Continue with Apple'}</span>
              </button>
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => {
                      if (!email) return;
                      const emailError = validateDeliverableEmail(email);
                      setError(emailError ?? '');
                    }}
                    required
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-dropsugar-green focus:border-transparent text-gray-900"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$"
                    title="Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)"
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-dropsugar-green focus:border-transparent text-gray-900"
                    placeholder="••••••••"
                  />
                </div>
                {isSignUp && (
                  <p className="mt-1 text-xs text-gray-500">
                    Must be at least 8 characters with uppercase, lowercase, number, and special character (@$!%*?&)
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-dropsugar-green hover:bg-dropsugar-green-dark disabled:bg-dropsugar-green/50 text-white font-semibold py-3 rounded-xl shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed"
              >
                {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError('');
                }}
                className="text-sm text-dropsugar-green hover:text-dropsugar-green-dark font-medium"
              >
                {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
